/**
 * Shared-SKU mapping surface types + helpers (BLU-711 Part B Stage 2 PR3).
 *
 * Everything infers off `RouterOutputs` so the UI can never drift from the
 * backend contract (mirrors `lib/types.ts`). The grouping helper implements
 * the ONE-CALL-PER-GROUP constraint's client half: an election batch for a
 * blank code must contain EVERY variant of that code, so the UI's unit of
 * election is the blank-code group, never a single variant.
 */
import type { RouterOutputs } from '@/lib/trpc';
import type { PreviewProduct, PreviewVariant } from './types';

/** Mapping filter for the Products tab — applied SERVER-side, mirrors StatusFilter/SyncFilter. */
export type MappingFilter = 'all' | 'mapped' | 'pending' | 'unmapped' | 'attention';

/**
 * PR #1667 review blocker: link-state procedures are tRPC GET queries behind
 * `httpBatchLink({ maxURLLength: 2083 })`, and a single op over the cap is
 * REJECTED client-side ("Input is too big for a single dispatch") — measured
 * ceiling ~31 encoded variant GIDs (~62 chars each). Every
 * `getVariantSharedSkuLinks` / `previewVariantSharedSkuLinkRemoval` call
 * must chunk to this size (the backend accepts 500/250 per call, but the
 * transport does not).
 */
export const LINK_STATE_GID_CHUNK = 30;

export function chunkGids(gids: readonly string[], size = LINK_STATE_GID_CHUNK): string[][] {
  const out: string[][] = [];
  for (let i = 0; i < gids.length; i += size) out.push(gids.slice(i, i + size));
  return out;
}

export type VariantSharedSkuLinksResult = RouterOutputs['productSync']['getVariantSharedSkuLinks'];
export type SharedLinkState = VariantSharedSkuLinksResult['states'][number];
export type SharedLinkAttentionReason = NonNullable<SharedLinkState['attentionReason']>;

export type SharedStockOwner = RouterOutputs['inventory']['listSharedStockOwners']['owners'][number];
export type OwnerSharedCatalogItem = RouterOutputs['inventory']['listOwnerSharedCatalog']['items'][number];

export type UnelectionPreview = RouterOutputs['productSync']['previewVariantSharedSkuLinkRemoval'];

/**
 * Stable display order for attention reasons — matches the backend
 * classifier's priority order (`classifyLinkStates`), so the most severe
 * reason always lists first. Includes `target_claimed` (PR3 audit fix).
 */
export const ATTENTION_REASON_ORDER: readonly SharedLinkAttentionReason[] = [
  'connection_inactive',
  'target_deleted',
  'target_unshared',
  'target_claimed',
  'code_drift',
  'mapping_conflict',
  'mapping_missing',
];

export function sortAttentionReasons(
  reasons: readonly SharedLinkAttentionReason[],
): SharedLinkAttentionReason[] {
  const rank = new Map(ATTENTION_REASON_ORDER.map((r, i) => [r, i]));
  return [...new Set(reasons)].sort(
    (a, b) => (rank.get(a) ?? ATTENTION_REASON_ORDER.length) - (rank.get(b) ?? ATTENTION_REASON_ORDER.length),
  );
}

export interface BlankCodeGroup {
  /** The derived blank base-garment code — the election's `baseSkuSnapshot`. */
  baseSku: string;
  variants: PreviewVariant[];
  /** Shopify variant GIDs — the WHOLE group, submitted in ONE `setVariantSharedSkuLinks` call. */
  gids: string[];
}

export interface GroupedVariants {
  groups: BlankCodeGroup[];
  /** Variants whose SKU derivation failed — they carry no blank code, so they cannot be elected. */
  underivedCount: number;
}

/**
 * Group a product's variants by derived blank code. Ordering is
 * first-appearance (the variant list's own order), so groups render in the
 * same order the variant matrix lists colours.
 */
export function groupVariantsByBaseSku(product: PreviewProduct): GroupedVariants {
  const byCode = new Map<string, BlankCodeGroup>();
  let underivedCount = 0;
  for (const variant of product.variants) {
    if (!variant.derived.ok) {
      underivedCount += 1;
      continue;
    }
    const baseSku = variant.derived.baseSku;
    let group = byCode.get(baseSku);
    if (!group) {
      group = { baseSku, variants: [], gids: [] };
      byCode.set(baseSku, group);
    }
    group.variants.push(variant);
    group.gids.push(variant.id);
  }
  return { groups: [...byCode.values()], underivedCount };
}

export interface GroupLinkSummary {
  mapped: number;
  pending: number;
  attention: number;
  unmapped: number;
  reasons: SharedLinkAttentionReason[];
  /** The elected target's live catalog code, when any state row carries one. */
  sharedSkuCode: string | null;
  /** The elected target's shared SKU id, when any state row carries one. */
  sharedSkuId: string | null;
  /** Owner org the group's elections point at, when known. */
  ownerOrganizationId: string | null;
  /** GIDs in this group that actually hold an election (removal targets). */
  electedGids: string[];
}

/** Aggregate a group's per-variant link states into one display summary. */
export function summarizeGroupLinks(
  group: BlankCodeGroup,
  stateByGid: ReadonlyMap<string, SharedLinkState>,
): GroupLinkSummary {
  const summary: GroupLinkSummary = {
    mapped: 0,
    pending: 0,
    attention: 0,
    unmapped: 0,
    reasons: [],
    sharedSkuCode: null,
    sharedSkuId: null,
    ownerOrganizationId: null,
    electedGids: [],
  };
  const reasons: SharedLinkAttentionReason[] = [];
  for (const gid of group.gids) {
    const state = stateByGid.get(gid);
    switch (state?.state) {
      case 'mapped':
        summary.mapped += 1;
        break;
      case 'elected_pending_sync':
        summary.pending += 1;
        break;
      case 'attention':
        summary.attention += 1;
        if (state.attentionReason) reasons.push(state.attentionReason);
        break;
      default:
        summary.unmapped += 1;
    }
    if (state && state.state !== 'unmapped') {
      summary.electedGids.push(gid);
      if (!summary.sharedSkuCode && state.sharedSkuCode) summary.sharedSkuCode = state.sharedSkuCode;
      if (!summary.sharedSkuId && state.sharedSkuId) summary.sharedSkuId = state.sharedSkuId;
      if (!summary.ownerOrganizationId && state.ownerOrganizationId) {
        summary.ownerOrganizationId = state.ownerOrganizationId;
      }
    }
  }
  summary.reasons = sortAttentionReasons(reasons);
  return summary;
}
